import { CustomerEvent } from '@demandlocal/utils/lib/mongo/models/CustomerEvents'
import type { SaasAudience } from '@demandlocal/utils/lib/mongo/models/SaasAudience'
import { useGetCustomerByID } from '../../../../../api/dealership/customers/useGetCustomerByID'
import React, { useMemo, useState } from 'react'
import { Navigate, Outlet, Route, Routes, useParams } from 'react-router'
import { Users, Plus, UserMinus } from 'lucide-react'
import dayjs from 'dayjs'
import classNames from 'classnames'
import { LoadingComponent, Pill } from '@demandlocal/components'
import { match } from 'ts-pattern'
import { useDealership } from '../../../../../store/zustand/dealershipStore'
import { useGetAgencyByAgencyId } from '../../../../../api/agencies/useGetAgencyByAgencyId'
import { useGetOverallStats } from '../../../../../api/dealership/useGetOverallStats'
import MyAudiences from '../../Audiences/MyAudiences'
import PageHeader from '../../../../../components/PageHeader/PageHeader'
import { ParsedClickHouseCustomerEvent } from '../../../../../types/CustomerEvent'

interface AudienceEvent extends CustomerEvent {
  event_data: Record<string, unknown> & {
    audience?: SaasAudience
  }
}

const getEventTypeDisplay = (
  type: ParsedClickHouseCustomerEvent<'audience_event'>['event_type'],
): string => {
  const displays = {
    audience_customer_added: 'Added to Audience',
    audience_customer_removed: 'Removed from Audience',
  }
  return displays[type]
}

const getEventIcon = (
  eventType: ParsedClickHouseCustomerEvent<'audience_event'>['event_type'],
): JSX.Element => {
  return eventType === 'audience_customer_added' ? (
    <Plus className="h-4 w-4" />
  ) : (
    <UserMinus className="h-4 w-4" />
  )
}

const getEventColor = (
  eventType: ParsedClickHouseCustomerEvent<'audience_event'>['event_type'],
): 'text-green-600' | 'text-red-600' => {
  return eventType === 'audience_customer_added' ? 'text-green-600' : 'text-red-600'
}

const EmptyState = ({ type }: { type: 'all' | 'complete' | 'draft' }): JSX.Element => {
  const messages = {
    all: {
      title: 'No Audience Events',
      description: 'This customer has not been added to or removed from any audiences.',
    },
    complete: {
      title: 'No Complete Audiences',
      description: 'This customer has not been added to any completed audiences.',
    },
    draft: {
      title: 'No Draft Audiences',
      description: 'This customer has not been added to any draft audiences.',
    },
  }

  return (
    <div className="flex flex-col items-center justify-center p-8 text-center bg-white rounded-lg border border-gray-200 shadow-sm">
      <Users className="h-10 w-10 text-gray-400 mb-3" />
      <h3 className="text-base font-semibold text-gray-900 mb-1">
        {messages[type].title}
      </h3>
      <p className="text-sm text-gray-500">{messages[type].description}</p>
    </div>
  )
}

export default function Audiences(): JSX.Element {
  const { customer_id } = useParams()
  const { data: customer } = useGetCustomerByID(customer_id)
  const [audienceType, setAudienceType] = useState<'draft' | 'complete' | 'all'>('all')

  const customerAudiences = useMemo(() => {
    return customer?.events
      ?.filter(
        (event) =>
          event.event_type === 'audience_customer_added' ||
          event.event_type === 'audience_customer_removed',
      )
      ?.filter((audienceEvent: ParsedClickHouseCustomerEvent<'audience_event'>) => {
        if (audienceType === 'all') return true
        if (audienceType === 'complete')
          return audienceEvent.event_data.audience?.creation_status === 'complete'
        return audienceEvent.event_data.audience?.creation_status === 'draft'
      })
  }, [audienceType, customer?.events])

  if (!customer) return <LoadingComponent title={'Loading customer'} />

  const hasAudiences = customerAudiences?.some(
    (event) =>
      event.event_type === 'audience_customer_added' ||
      event.event_type === 'audience_customer_removed',
  )
  return (
    <div className="flex flex-1 flex-col h-full">
      {/* Sticky header with tabs */}
      <div className="sticky top-0 bg-white z-10">
        <div className="px-4 py-2">
          <ul className="flex flex-wrap text-sm font-medium text-center gap-2">
            <li
              onClick={() => setAudienceType('all')}
              className={classNames(
                'px-4 py-2 rounded-lg cursor-pointer transition-colors duration-200',
                {
                  'bg-primary-persian-blue text-white': audienceType === 'all',
                  'text-web-gray hover:bg-gray-100': audienceType !== 'all',
                },
              )}>
              All
            </li>
            <li
              onClick={() => setAudienceType('complete')}
              className={classNames(
                'px-4 py-2 rounded-lg cursor-pointer transition-colors duration-200',
                {
                  'bg-primary-persian-blue text-white': audienceType === 'complete',
                  'text-web-gray hover:bg-gray-100': audienceType !== 'complete',
                },
              )}>
              Complete
            </li>
            <li
              onClick={() => setAudienceType('draft')}
              className={classNames(
                'px-4 py-2 rounded-lg cursor-pointer transition-colors duration-200',
                {
                  'bg-primary-persian-blue text-white': audienceType === 'draft',
                  'text-web-gray hover:bg-gray-100': audienceType !== 'draft',
                },
              )}>
              Draft
            </li>
          </ul>
        </div>
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex flex-col gap-4">
          {!hasAudiences ? (
            <EmptyState type="all" />
          ) : !customerAudiences?.length ? (
            <EmptyState type={audienceType} />
          ) : (
            customerAudiences?.map(
              (audienceEvent: ParsedClickHouseCustomerEvent<'audience_event'>) => {
                const audience = audienceEvent.event_data.audience
                const isDraft = audience?.creation_status === 'draft'

                if (!audience) return null
                return (
                  <div
                    key={audienceEvent.id}
                    className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden transition-shadow duration-200 hover:shadow-md">
                    <div className="p-4">
                      {/* Header with icon and timestamp */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div
                            className={classNames(
                              'p-2 rounded-full bg-gray-50',
                              getEventColor(audienceEvent.event_type),
                            )}>
                            {getEventIcon(audienceEvent.event_type)}
                          </div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-900">
                              {getEventTypeDisplay(audienceEvent.event_type)}
                            </h3>
                            {isDraft && <Pill type="warning">Draft</Pill>}
                          </div>
                        </div>
                        <span className="text-sm text-gray-500">
                          {dayjs(audienceEvent.event_date).format('MMM D, YYYY h:mm A')}
                        </span>
                      </div>

                      <div className="mt-4 border-t border-gray-100 pt-4">
                        <div className="space-y-4">
                          <div>
                            <h4 className="text-gray-500 font-medium text-sm mb-2">
                              {audience.audience_name || 'Unnamed Audience'}
                            </h4>
                            <Pill
                              type={
                                match(audience.audience_type)
                                  .with('custom', () => 'gray')
                                  .with('sales', () => 'success')
                                  .with('service', () => 'info-purple')
                                  .with('leads', () => 'warning')
                                  .otherwise(() => 'error') as
                                  | 'gray'
                                  | 'success'
                                  | 'info-purple'
                                  | 'warning'
                                  | 'error'
                              }>
                              {audience.audience_type}
                            </Pill>
                          </div>

                          {audience.description && (
                            <p className="text-sm text-gray-600">
                              {audience.description}
                            </p>
                          )}

                          <div className="flex flex-wrap gap-2">
                            <Pill type="gray">
                              <Users className="w-3 h-3 mr-1" />
                              {audience.audience_size?.toLocaleString()} members
                            </Pill>
                            {audience.created_by && (
                              <Pill type="gray">
                                Created by {audience.created_by.first_name}{' '}
                                {audience.created_by.last_name}
                              </Pill>
                            )}
                          </div>

                          {audience.queryLog && audience.queryLog.length > 0 && (
                            <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                              <p className="text-xs text-gray-500 mb-1">
                                Query Description:
                              </p>
                              <p className="text-sm text-gray-900">
                                {audience.queryLog[0].description}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              },
            )
          )}
        </div>
      </div>
    </div>
  )
}
